import { z } from 'zod';

import { graderWithFallback } from '../../constants/graders';
import { pathOrUrl, pathOrUrlOrEmpty } from '../../utils';
import { TIERS } from '../constants/tiers';
import { CardUpdatePatchSchema, CardUpdateStateSchema } from './CardUpdateState';
import { CardInfoSchema } from './reveals/CardInfo';
import { CutawayASchema } from './reveals/CutawayA';
import { CutawayBSchema } from './reveals/CutawayB';
import { CutawayCSchema } from './reveals/CutawayC';
import { PlayerInfoSchema } from './reveals/PlayerInfo';
import { TeamInfoSchema } from './reveals/TeamInfo';

export const CardDetailTextSchema = z.object({
    primary: z.string().optional(),
    secondary: z.string().optional(),
    tertiary: z.string().optional(),
});

export const CardPlayerNameSchema = z.object({
    first: z.string(),
    last: z.string(),
});

export const CardTeamNameSchema = z.object({
    line1: z.string(),
    line2: z.string(),
});

export const CardMetaSchema = z.object({
    autographed: z.boolean().default(false),
    rookie: z.boolean().default(false),
});

export const CardSchema = z.object({
    // Card info
    id: z.string(),
    title: z.string(),
    condition: z.string().optional(), // optional for raw cards
    serialNumber: z.number().nullish(),
    serialTotal: z.number().nullish(), // total number of cards in the set (serialNumber / serialTotal)
    imageFront: pathOrUrlOrEmpty, // front of the card
    imageBack: pathOrUrlOrEmpty, // back of the card
    gradeBadge: pathOrUrlOrEmpty.nullish(), // grade badge: needs to be transparent png
    preRevealSticker: pathOrUrl.optional(),
    parallel: z.string().optional(), // optional for raw cards
    tier: TIERS,
    grader: graderWithFallback, // optional - raw cards won't have this
    detailText: CardDetailTextSchema.optional(),
    cardMeta: CardMetaSchema.optional(),

    // Player
    playerName: CardPlayerNameSchema.nullish(),

    playerNumber: z.number().nullish(),

    // Team
    teamName: CardTeamNameSchema.nullish(),

    teamBadge: pathOrUrl.nullish(),
    teamYear: z.number().nullish(),

    // List of reveals (can be empty for wax pack cards)
    reveals: z
        .array(
            z.union([
                TeamInfoSchema,
                PlayerInfoSchema,
                CardInfoSchema,
                CutawayASchema,
                CutawayBSchema,
                CutawayCSchema,
            ])
        )
        .default([]),
    updateCardData: CardUpdateStateSchema,
    orientation: z.enum(['portrait', 'landscape']).optional(),
    direction: z
        .union([
            z.enum(['top', 'bottom', 'left', 'right']),
            z.literal(0),
            z.literal(90),
            z.literal(-90),
            z.literal(180),
        ])
        .optional(),

    color: z.string().optional(), // For testing purposes
});

export const CardPatchSchema = CardSchema.partial().extend({
    updateCardData: CardUpdatePatchSchema.optional(),
});

export type Card = z.infer<typeof CardSchema>;
export type CardMeta = z.infer<typeof CardMetaSchema>;
